import React, { useState } from 'react';
import { View, Text, TextInput, ScrollView, StyleSheet, TouchableOpacity, SafeAreaView, Modal } from 'react-native';
import SvgIcon from '../components/SvgIcon';
import apiClient from '../services/api';
import colors from '../theme/colors';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const COUNTRIES = ['India'];
const COUNTRY_CODES = ['91', '1', '44'];
const GENDERS = ['Male', 'Female', 'Other'];

const MediatorRegistrationScreen = ({ navigation }) => {
  const [form, setForm] = useState({
    countryCode: '91',
    mobileNumber: '',
    email: '',
    name: '',
    surName: '',
    dateOfBirth: '',
    gender: '',
    bureauName: '',
    bureauRegNumber: '',
    country: 'India',
    state: '',
    city: '',
    address: '',
    area: '',
    pinCode: '',
    referrerCountryCode: '91',
    referredBy: 0,
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [referrerMobile, setReferrerMobile] = useState('');
  const [referrerVerified, setReferrerVerified] = useState(false);
  const [referrerDetails, setReferrerDetails] = useState({ name: '', bureauName: '' });
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCountryCodeModal, setShowCountryCodeModal] = useState(false);
  const [showReferrerCountryCodeModal, setShowReferrerCountryCodeModal] = useState(false);
  const [showPasswordHint, setShowPasswordHint] = useState(false);
  const [dialog, setDialog] = useState({ visible: false, type: '', title: '', message: '', onConfirm: null });
  const [mobileNumberFocused, setMobileNumberFocused] = useState(false);
  const [referrerMobileFocused, setReferrerMobileFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [surNameFocused, setSurNameFocused] = useState(false);
  const [dateOfBirthFocused, setDateOfBirthFocused] = useState(false);
  const [bureauNameFocused, setBureauNameFocused] = useState(false);
  const [bureauRegNumberFocused, setBureauRegNumberFocused] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);
  const [areaFocused, setAreaFocused] = useState(false);
  const [cityFocused, setCityFocused] = useState(false);
  const [pinCodeFocused, setPinCodeFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (key, value) => {
    setForm(prevForm => ({ ...prevForm, [key]: value }));
  };

  const showDialog = (type, title, message, onConfirm = null) => {
    setDialog({ visible: true, type, title, message, onConfirm });
  };

  const hideDialog = () => {
    setDialog({ visible: false, type: '', title: '', message: '', onConfirm: null });
  };

  const handleDialogConfirm = () => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
    hideDialog();
  };

  const handleClear = () => {
    setForm({
      countryCode: '91',
      mobileNumber: '',
      email: '',
      name: '',
      surName: '',
      dateOfBirth: '',
      gender: '',
      bureauName: '',
      bureauRegNumber: '',
      country: 'India',
      state: '',
      city: '',
      address: '',
      area: '',
      pinCode: '',
      referrerCountryCode: '91',
      referredBy: 0,
      password: '',
      confirmPassword: '',
    });
    setReferrerMobile('');
    setReferrerVerified(false);
    setReferrerDetails({ name: '', bureauName: '' });
  };

  const verifyReferrer = async () => {
    // Clear any existing dialogs first
    hideDialog();
    
    if (!referrerMobile || referrerMobile.trim() === '') {
      setTimeout(() => {
        showDialog('error', 'Validation Error', 'Please enter referrer mobile number.');
      }, 50);
      return;
    }

    if (!/^\d{10}$/.test(referrerMobile)) {
      setTimeout(() => {
        showDialog('error', 'Validation Error', 'Please enter a valid 10-digit mobile number.');
      }, 50);
      return;
    }

    setIsVerifying(true);
    try {
      const response = await apiClient.get(`/api/Mediator/find-by-contact`, {
        params: {
          mobileNumber: referrerMobile,
          countryCode: form.referrerCountryCode
        }
      });

      if (response.data && response.data.mediatorId) {
        setForm(prevForm => ({ ...prevForm, referredBy: response.data.mediatorId }));
        setReferrerVerified(true);
        setReferrerDetails({
          name: response.data.name || '',
          bureauName: response.data.bureauName || ''
        });
        // Show success dialog after state is set
        setTimeout(() => {
          showDialog('success', 'Success', `Referrer verified: ${response.data.name || 'Mediator'}`);
        }, 100);
      } else {
        setTimeout(() => {
          showDialog('error', 'Error', 'Referrer not found. Please check the mobile number.');
        }, 100);
      }
    } catch (error) {
      setTimeout(() => {
        showDialog('error', 'Error', 'Failed to verify referrer. Please try again.');
      }, 100);
    } finally {
      setIsVerifying(false);
    }
  };

  const resetReferrer = () => {
    setReferrerMobile('');
    setReferrerVerified(false);
    setReferrerDetails({ name: '', bureauName: '' });
    setForm(prevForm => ({ ...prevForm, referredBy: 0 }));
  };

  const validateForm = () => {
    console.log('Validating registration form:', form);
    
    const requiredFields = [
      { key: 'mobileNumber', label: 'Mobile Number' },
      { key: 'email', label: 'Email' },
      { key: 'name', label: 'First Name' },
      { key: 'surName', label: 'Last Name' },
      { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'bureauName', label: 'Bureau Name' },
      { key: 'address', label: 'Address' },
      { key: 'area', label: 'Area' },
      { key: 'city', label: 'City/Town/Village' },
      { key: 'state', label: 'State' },
      { key: 'country', label: 'Country' },
      { key: 'pinCode', label: 'Postal Code' },
      { key: 'password', label: 'Password' },
      { key: 'confirmPassword', label: 'Confirm Password' },
    ];

    for (let field of requiredFields) {
      if (!form[field.key] || form[field.key].trim() === '') {
        console.log(`Validation failed: ${field.label} is empty`);
        showDialog('error', 'Validation Error', `${field.label} is required.`);
        return false;
      }
    }

    // Name validation (letters, spaces, hyphens, apostrophes only)
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(form.name)) {
      showDialog('error', 'Validation Error', 'First Name should contain only letters.');
      return false;
    }
    if (!nameRegex.test(form.surName)) {
      showDialog('error', 'Validation Error', 'Last Name should contain only letters.');
      return false;
    }

    // Name length validation
    if (form.name.trim().length < 2 || form.name.trim().length > 50) {
      showDialog('error', 'Validation Error', 'First Name must be between 2 and 50 characters.');
      return false;
    }
    if (form.surName.trim().length < 2 || form.surName.trim().length > 50) {
      showDialog('error', 'Validation Error', 'Last Name must be between 2 and 50 characters.');
      return false;
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(form.email)) {
      showDialog('error', 'Validation Error', 'Please enter a valid email address.');
      return false;
    }

    // Mobile number validation
    if (!/^\d{10}$/.test(form.mobileNumber)) {
      showDialog('error', 'Validation Error', 'Please enter a valid 10-digit mobile number.');
      return false;
    }

    // Date of Birth validation and age check
    if (!/^\d{2}-\d{2}-\d{4}$/.test(form.dateOfBirth)) {
      showDialog('error', 'Validation Error', 'Date of Birth must be in DD-MM-YYYY format.');
      return false;
    }
    
    // Parse DD-MM-YYYY format
    const [day, month, year] = form.dateOfBirth.split('-');
    const dob = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear() - 
                (today.getMonth() < dob.getMonth() || 
                 (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate()) ? 1 : 0);
    if (isNaN(dob.getTime())) {
      showDialog('error', 'Validation Error', 'Please enter a valid date of birth.');
      return false;
    }
    if (age < 18) {
      showDialog('error', 'Validation Error', 'You must be at least 18 years old to register.');
      return false;
    }
    if (age > 100) {
      showDialog('error', 'Validation Error', 'Please enter a valid date of birth.');
      return false;
    }

    // Bureau name validation
    if (form.bureauName.trim().length < 3 || form.bureauName.trim().length > 100) {
      showDialog('error', 'Validation Error', 'Bureau Name must be between 3 and 100 characters.');
      return false;
    }

    // Address validations
    if (form.address.trim().length < 5 || form.address.trim().length > 200) {
      showDialog('error', 'Validation Error', 'Address must be between 5 and 200 characters.');
      return false;
    }
    if (form.area.trim().length < 2 || form.area.trim().length > 100) {
      showDialog('error', 'Validation Error', 'Area must be between 2 and 100 characters.');
      return false;
    }
    if (form.city.trim().length < 2 || form.city.trim().length > 100) {
      showDialog('error', 'Validation Error', 'City must be between 2 and 100 characters.');
      return false;
    }

    // Pin code validation (6 digits)
    if (!/^\d{6}$/.test(form.pinCode)) {
      showDialog('error', 'Validation Error', 'Please enter a valid 6-digit postal code.');
      return false;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(form.password)) {
      showDialog('error', 'Validation Error', 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.');
      return false;
    }

    if (form.password !== form.confirmPassword) {
      showDialog('error', 'Validation Error', 'Password and Confirm Password do not match.');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      // Convert DD-MM-YYYY to YYYY-MM-DD for API
      const [day, month, year] = form.dateOfBirth.split('-');
      const dobForApi = `${year}-${month}-${day}`;
      
      const payload = {
        countryCode: form.countryCode,
        mobileNumber: form.mobileNumber,
        email: form.email,
        name: form.name,
        surName: form.surName,
        dateOfBirth: dobForApi,
        gender: form.gender,
        bureauName: form.bureauName,
        bureauRegNumber: form.bureauRegNumber || '',
        country: form.country,
        state: form.state,
        city: form.city,
        address: form.address,
        area: form.area,
        pinCode: form.pinCode,
        referrerCountryCode: form.referrerCountryCode,
        password: form.password,
        referredBy: form.referredBy,
      };
      console.log('Submitting payload:', payload);
      const res = await apiClient.post('/api/Auth/register/mediator', payload);
      console.log('API Response:', res.data);

      const mediatorName = res?.data?.name && res?.data?.surName
        ? `${res.data.name} ${res.data.surName}`
        : res?.data?.name || 'New mediator';

      showDialog(
        'success',
        'Success',
        `Mediator registration for ${mediatorName} completed successfully!`,
        () => {
          console.log('Navigating to Home');
          navigation.navigate('Home');
        }
      );
    } catch (error) {
      console.error('Registration error:', error);
      showDialog('error', 'Error', error?.response?.data?.message || error.message || 'Failed to register mediator');
    } finally {
      setIsLoading(false);
    }
  };

  const CustomDropdown = ({ label, value, onPress, placeholder }) => (
    <TouchableOpacity style={styles.dropdown} onPress={onPress}>
      <Text style={value ? styles.dropdownText : styles.dropdownPlaceholder}>
        {value || placeholder}
      </Text>
      <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
      <View style={styles.headerSection}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <SvgIcon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={styles.title}>Mediator Registration</Text>
        <Text style={styles.requiredNote}>* indicates required fields</Text>
      </View>
      <ScrollView contentContainerStyle={styles.container}>
        {/* Personal Information Section */}
        <Text style={styles.sectionHeader}>Personal Information</Text>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Mobile Number (Used for Login) *</Text>
          <View style={[styles.groupedInputContainer, mobileNumberFocused && styles.groupedInputContainerFocused]}>
            <View style={styles.countryCodeButtonGrouped}>
              <Text style={styles.countryCodeText}>{form.countryCode}</Text>
            </View>
            <TextInput
              style={styles.phoneInputGrouped}
              placeholder="Enter 10 digit Mobile Number"
              placeholderTextColor="#9CA3AF"
              value={form.mobileNumber}
              onChangeText={v => handleChange('mobileNumber', v)}
              keyboardType="phone-pad"
              maxLength={10}
              onFocus={() => setMobileNumberFocused(true)}
              onBlur={() => setMobileNumberFocused(false)}
            />
          </View>
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Email *</Text>
          <TextInput
            style={[styles.input, emailFocused && styles.inputFocused]}
            placeholder="Enter Email"
            placeholderTextColor="#9CA3AF"
            value={form.email}
            onChangeText={v => handleChange('email', v)}
            keyboardType="email-address"
            autoCapitalize="none"
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>First Name *</Text>
          <TextInput
            style={[styles.input, nameFocused && styles.inputFocused]}
            placeholder="Enter your First Name"
            placeholderTextColor="#9CA3AF"
            value={form.name}
            onChangeText={v => handleChange('name', v)}
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Last Name *</Text>
          <TextInput
            style={[styles.input, surNameFocused && styles.inputFocused]}
            placeholder="Enter your Last Name (Sur Name)"
            placeholderTextColor="#9CA3AF"
            value={form.surName}
            onChangeText={v => handleChange('surName', v)}
            onFocus={() => setSurNameFocused(true)}
            onBlur={() => setSurNameFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Date of Birth *</Text>
          <TextInput
            style={[styles.input, dateOfBirthFocused && styles.inputFocused]}
            placeholder="dd-mm-yyyy"
            placeholderTextColor="#9CA3AF"
            value={form.dateOfBirth}
            onChangeText={v => handleChange('dateOfBirth', v)}
            onFocus={() => setDateOfBirthFocused(true)}
            onBlur={() => setDateOfBirthFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Gender *</Text>
          <CustomDropdown
            value={form.gender}
            placeholder="Select Gender"
            onPress={() => setShowGenderModal(true)}
          />
        </View>

        {/* Bureau Information Section */}
        <Text style={styles.sectionHeader}>Bureau Information</Text>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Bureau Name *</Text>
          <TextInput
            style={[styles.input, bureauNameFocused && styles.inputFocused]}
            placeholder="Enter Bureau Name"
            placeholderTextColor="#9CA3AF"
            value={form.bureauName}
            onChangeText={v => handleChange('bureauName', v)}
            onFocus={() => setBureauNameFocused(true)}
            onBlur={() => setBureauNameFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Registration Number (Issued by Govt)</Text>
          <TextInput
            style={[styles.input, bureauRegNumberFocused && styles.inputFocused]}
            placeholder="Enter Bureau Registration Number (optional)"
            placeholderTextColor="#9CA3AF"
            value={form.bureauRegNumber}
            onChangeText={v => handleChange('bureauRegNumber', v)}
            onFocus={() => setBureauRegNumberFocused(true)}
            onBlur={() => setBureauRegNumberFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Address *</Text>
          <TextInput
            style={[styles.input, addressFocused && styles.inputFocused]}
            placeholder="Enter Address (House No, Street, etc.)"
            placeholderTextColor="#9CA3AF"
            value={form.address}
            onChangeText={v => handleChange('address', v)}
            onFocus={() => setAddressFocused(true)}
            onBlur={() => setAddressFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Area *</Text>
          <TextInput
            style={[styles.input, areaFocused && styles.inputFocused]}
            placeholder="Enter Area"
            placeholderTextColor="#9CA3AF"
            value={form.area}
            onChangeText={v => handleChange('area', v)}
            onFocus={() => setAreaFocused(true)}
            onBlur={() => setAreaFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>City/Town/Village *</Text>
          <TextInput
            style={[styles.input, cityFocused && styles.inputFocused]}
            placeholder="Enter City/Town/Village"
            placeholderTextColor="#9CA3AF"
            value={form.city}
            onChangeText={v => handleChange('city', v)}
            onFocus={() => setCityFocused(true)}
            onBlur={() => setCityFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>State *</Text>
          <CustomDropdown
            value={form.state}
            placeholder="Select State"
            onPress={() => setShowStateModal(true)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Country *</Text>
          <CustomDropdown
            value={form.country}
            placeholder="Select Country"
            onPress={() => setShowCountryModal(true)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Postal Code *</Text>
          <TextInput
            style={[styles.input, pinCodeFocused && styles.inputFocused]}
            placeholder="Enter Postal Code"
            placeholderTextColor="#9CA3AF"
            value={form.pinCode}
            onChangeText={v => handleChange('pinCode', v)}
            keyboardType="numeric"
            maxLength={6}
            onFocus={() => setPinCodeFocused(true)}
            onBlur={() => setPinCodeFocused(false)}
          />
        </View>

        {/* Referral Information Section */}
        <Text style={styles.sectionHeader}>Referral Information</Text>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Referred By (optional)</Text>
          <View style={[styles.groupedInputContainer, referrerMobileFocused && styles.groupedInputContainerFocused]}>
            <View style={styles.countryCodeButtonGrouped}>
              <Text style={styles.countryCodeText}>{form.referrerCountryCode}</Text>
            </View>
            <TextInput
              style={styles.phoneInputGrouped}
              placeholder="Enter referrer mobile number"
              placeholderTextColor="#9CA3AF"
              value={referrerMobile}
              onChangeText={setReferrerMobile}
              keyboardType="phone-pad"
              maxLength={10}
              editable={!referrerVerified}
              onFocus={() => setReferrerMobileFocused(true)}
              onBlur={() => setReferrerMobileFocused(false)}
            />
            <TouchableOpacity
              style={[styles.verifyButtonInline, referrerVerified && styles.verifyButtonSuccess]}
              onPress={verifyReferrer}
              disabled={isVerifying || referrerVerified}
            >
              {referrerVerified ? (
                <SvgIcon name="checkmark" size={16} color="#fff" />
              ) : (
                <Text style={styles.verifyButtonTextSmall}>
                  {isVerifying ? '...' : 'Verify'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {referrerVerified && referrerDetails.name && (
          <View style={styles.referrerDetailsContainer}>
            <View style={styles.referrerDetailsHeader}>
              <Text style={styles.referrerDetailsTitle}>Verified Referrer</Text>
              <TouchableOpacity
                style={styles.changeButton}
                onPress={resetReferrer}
              >
                <Text style={styles.changeButtonText}>Change</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.referrerDetailRow}>
              <SvgIcon name="person-outline" size={16} color={colors.secondary} />
              <Text style={styles.referrerDetailLabel}>Referrer Name: </Text>
              <Text style={styles.referrerDetailValue}>{referrerDetails.name}</Text>
            </View>
            {referrerDetails.bureauName && (
              <View style={styles.referrerDetailRow}>
                <SvgIcon name="business-outline" size={16} color={colors.secondary} />
                <Text style={styles.referrerDetailLabel}>Bureau Name: </Text>
                <Text style={styles.referrerDetailValue}>{referrerDetails.bureauName}</Text>
              </View>
            )}
          </View>
        )}

        {/* Account Security Section */}
        <Text style={styles.sectionHeader}>Account Security</Text>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Password *</Text>
          <View style={[styles.passwordContainer, passwordFocused && styles.passwordContainerFocused]}>
            <TextInput
              style={styles.passwordInput}
              placeholder="Enter password"
              placeholderTextColor="#9CA3AF"
              value={form.password}
              onChangeText={v => handleChange('password', v)}
              secureTextEntry={!showPassword}
              onFocus={() => {
                setPasswordFocused(true);
                setShowPasswordHint(true);
              }}
              onBlur={() => {
                setPasswordFocused(false);
                setShowPasswordHint(false);
              }}
            />
            <TouchableOpacity 
              onPress={() => setShowPassword(!showPassword)}
              style={styles.eyeIcon}
            >
              <SvgIcon 
                name={showPassword ? 'eye' : 'eye-off'} 
                size={20} 
                color="#9CA3AF" 
              />
            </TouchableOpacity>
          </View>
        </View>
        {showPasswordHint && (
          <Text style={styles.passwordHint}>
            Password must be at least 8 characters with uppercase, lowercase, number, and special character.
          </Text>
        )}

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Confirm Password *</Text>
          <View style={[styles.passwordContainer, confirmPasswordFocused && styles.passwordContainerFocused]}>
            <TextInput
              style={styles.passwordInput}
              placeholder="Confirm password"
              placeholderTextColor="#9CA3AF"
              value={form.confirmPassword}
              onChangeText={v => handleChange('confirmPassword', v)}
              secureTextEntry={!showConfirmPassword}
              onFocus={() => setConfirmPasswordFocused(true)}
              onBlur={() => setConfirmPasswordFocused(false)}
            />
            <TouchableOpacity 
              onPress={() => setShowConfirmPassword(!showConfirmPassword)}
              style={styles.eyeIcon}
            >
              <SvgIcon 
                name={showConfirmPassword ? 'eye' : 'eye-off'} 
                size={20} 
                color="#9CA3AF" 
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.button, styles.submitButton]}
            onPress={handleSubmit}
            disabled={isLoading}
          >
            <Text style={styles.buttonText}>{isLoading ? 'Submitting...' : 'Submit'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.clearButton]}
            onPress={handleClear}
            disabled={isLoading}
          >
            <Text style={styles.buttonText}>Clear</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Gender Modal */}
      <Modal visible={showGenderModal} transparent animationType="slide">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowGenderModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Gender</Text>
            {GENDERS.map((gender) => (
              <TouchableOpacity
                key={gender}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('gender', gender);
                  setShowGenderModal(false);
                }}
              >
                <Text style={styles.modalItemText}>{gender}</Text>
                {form.gender === gender && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* State Modal */}
      <Modal visible={showStateModal} transparent animationType="slide">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select State</Text>
            <ScrollView style={styles.modalScroll}>
              {INDIAN_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.modalItem}
                  onPress={() => {
                    handleChange('state', state);
                    setShowStateModal(false);
                  }}
                >
                  <Text style={styles.modalItemText}>{state}</Text>
                  {form.state === state && (
                    <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Country Modal */}
      <Modal visible={showCountryModal} transparent animationType="slide">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCountryModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Country</Text>
            {COUNTRIES.map((country) => (
              <TouchableOpacity
                key={country}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('country', country);
                  setShowCountryModal(false);
                }}
              >
                <Text style={styles.modalItemText}>{country}</Text>
                {form.country === country && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Country Code Modal */}
      <Modal visible={showCountryCodeModal} transparent animationType="slide">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCountryCodeModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Country Code</Text>
            {COUNTRY_CODES.map((code) => (
              <TouchableOpacity
                key={code}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('countryCode', code);
                  setShowCountryCodeModal(false);
                }}
              >
                <Text style={styles.modalItemText}>+{code}</Text>
                {form.countryCode === code && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Referrer Country Code Modal */}
      <Modal visible={showReferrerCountryCodeModal} transparent animationType="slide">
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowReferrerCountryCodeModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Country Code</Text>
            {COUNTRY_CODES.map((code) => (
              <TouchableOpacity
                key={code}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('referrerCountryCode', code);
                  setShowReferrerCountryCodeModal(false);
                }}
              >
                <Text style={styles.modalItemText}>+{code}</Text>
                {form.referrerCountryCode === code && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Custom Dialog Modal */}
      <Modal visible={dialog.visible} transparent animationType="fade">
        <View style={styles.dialogOverlay}>
          <View style={styles.dialogContainer}>
            <View style={[styles.dialogHeader, dialog.type === 'success' ? styles.dialogHeaderSuccess : styles.dialogHeaderError]}>
              <SvgIcon
                name={dialog.type === 'success' ? 'checkmark-circle' : 'alert-circle'}
                size={50}
                color="#fff"
              />
            </View>
            <View style={styles.dialogBody}>
              <Text style={styles.dialogTitle}>{dialog.title}</Text>
              <Text style={styles.dialogMessage}>{dialog.message}</Text>
              <TouchableOpacity
                style={[styles.dialogButton, dialog.type === 'success' ? styles.dialogButtonSuccess : styles.dialogButtonError]}
                onPress={handleDialogConfirm}
              >
                <Text style={styles.dialogButtonText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  headerSection: {
    backgroundColor: colors.secondary,
    padding: 20,
    alignItems: 'center',
  },
  backButton: {
    position: 'absolute',
    left: 15,
    top: 15,
    padding: 8,
    zIndex: 10,
  },
  container: {
    padding: 20,
    backgroundColor: '#FFFFFF',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
    marginBottom: 4,
  },
  requiredNote: {
    fontSize: 12,
    color: '#fff',
    textAlign: 'center',
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.secondary,
    marginTop: 20,
    marginBottom: 16,
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  floatingLabel: {
    position: 'absolute',
    left: 12,
    top: -10,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 4,
    fontSize: 12,
    fontWeight: '500',
    color: colors.primary,
    zIndex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.primary,
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: '#F9FAFB',
    fontSize: 14,
    color: colors.primary,
  },
  inputFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    paddingRight: 12,
  },
  passwordContainerFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  passwordInput: {
    flex: 1,
    padding: 12,
    paddingTop: 14,
    fontSize: 14,
    color: colors.primary,
  },
  eyeIcon: {
    padding: 4,
  },
  dropdown: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: '#F9FAFB',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: 14,
    color: colors.primary,
  },
  dropdownPlaceholder: {
    fontSize: 14,
    color: '#9CA3AF',
  },
  groupedInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 8,
  },
  groupedInputContainerFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  countryCodeButtonGrouped: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRightWidth: 1,
    borderRightColor: '#D1D5DB',
  },
  phoneInputGrouped: {
    flex: 1,
    fontSize: 14,
    color: colors.primary,
    paddingVertical: 8,
    outlineWidth: 0,
  },
  verifyButtonInline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.secondary,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 55,
  },
  phoneRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  countryCodeButton: {
    width: 80,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: 12,
  },
  countryCodeText: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '500',
  },
  phoneInput: {
    flex: 1,
    marginBottom: 0,
  },
  phoneInputReduced: {
    flex: 1,
    marginBottom: 0,
    marginRight: 6,
    maxWidth: '55%',
  },
  countryCodeButtonDisabled: {
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: '#E8E8E8',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginRight: 6,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.7,
    minWidth: 55,
  },
  verifyButton: {
    backgroundColor: colors.secondary,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  verifyButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  verifyButtonSmall: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.secondary,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 55,
  },
  verifyButtonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.secondary,
    borderRadius: 8,
    paddingVertical: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  verifyButtonSuccess: {
    backgroundColor: colors.success,
    borderColor: colors.success,
  },
  verifyButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  verifyButtonTextSmall: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '600',
  },
  referrerDetailsContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#F0FDF4',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.secondary,
  },
  referrerDetailsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  referrerDetailsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.secondary,
  },
  changeButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.secondary,
  },
  changeButtonText: {
    fontSize: 12,
    color: colors.secondary,
    fontWeight: '600',
  },
  referrerDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  referrerDetailLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 6,
  },
  referrerDetailValue: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '600',
    flex: 1,
  },
  passwordHint: {
    fontSize: 12,
    color: colors.secondary,
    marginBottom: 12,
    marginTop: -8,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
    marginBottom: 20,
  },
  button: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButton: {
    backgroundColor: colors.secondary,
  },
  clearButton: {
    backgroundColor: '#6c757d',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '70%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  modalScroll: {
    maxHeight: 400,
  },
  modalItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalItemText: {
    fontSize: 16,
    color: colors.text,
  },
  dialogOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialogContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  dialogHeader: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogHeaderSuccess: {
    backgroundColor: colors.success,
  },
  dialogHeaderError: {
    backgroundColor: colors.error,
  },
  dialogBody: {
    padding: 24,
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  dialogMessage: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  dialogButton: {
    paddingVertical: 12,
    paddingHorizontal: 40,
    borderRadius: 8,
    minWidth: 120,
  },
  dialogButtonSuccess: {
    backgroundColor: colors.success,
  },
  dialogButtonError: {
    backgroundColor: colors.error,
  },
  dialogButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default MediatorRegistrationScreen;
